import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { getTranslation } from '@nexelem/utils';
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_CRITICAL,
  FORMAT_TEXT_COMMAND,
  LexicalEditor,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IS_APPLE } from '../../shared/environment';
import { getSelectedNode } from '../../utils/getSelectedNode';
import { sanitizeUrl } from '../../utils/url';
import { FormattingDropDown } from '../FormattingDropDown';
import { IconButton } from '../IconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBold, faItalic, faLinkHorizontal, faUnderline } from '@fortawesome/pro-light-svg-icons';

type FontAppearanceFormSectionProps = {
  readonly disabled?: boolean;
  readonly editor: LexicalEditor;
};

export const FontAppearanceFormSection = ({ editor, disabled }: FontAppearanceFormSectionProps) => {
  const [isLink, setIsLink] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const { t: translate } = useTranslation();
  const t = useMemo(() => getTranslation(translate, 'components.richTextEditor.formatting'), [translate]);

  const insertLink = useCallback(() => {
    if (!isLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitizeUrl('https://'));
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [editor, isLink]);

  const update = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      // Update links
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true);
      } else {
        setIsLink(false);
      }
    }
  }, []);

  useEffect(
    () =>
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload) => {
          update();
          return false;
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
    [editor, update],
  );

  useEffect(() => {
    editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        update();
      });
    });
  }, [editor, update]);

  const onClickBold = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
  }, [editor]);

  const onClickItalic = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
  }, [editor]);

  const onClickUnderline = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
  }, [editor]);

  return (
    <div className="flex gap-1">
      <IconButton
        ariaLabel={t('bold.ariaLabel', { shortcut: IS_APPLE ? '⌘B' : 'Ctrl+B' })}
        disabled={disabled}
        isActive={isBold}
        StartIcon={<FontAwesomeIcon icon={faBold} />}
        title={t('bold.title', { shortcut: IS_APPLE ? '⌘B' : 'Ctrl+B' })}
        variant="text"
        onClick={onClickBold}
      />
      <IconButton
        ariaLabel={t('italic.ariaLabel', { shortcut: IS_APPLE ? '⌘I' : 'Ctrl+I' })}
        disabled={disabled}
        isActive={isItalic}
        StartIcon={<FontAwesomeIcon icon={faItalic} />}
        title={t('italic.title', { shortcut: IS_APPLE ? '⌘I' : 'Ctrl+I' })}
        variant="text"
        onClick={onClickItalic}
      />
      <IconButton
        ariaLabel={t('underline.ariaLabel', { shortcut: IS_APPLE ? '⌘U' : 'Ctrl+U' })}
        disabled={disabled}
        isActive={isUnderline}
        StartIcon={<FontAwesomeIcon icon={faUnderline} />}
        title={t('underline.title', { shortcut: IS_APPLE ? '⌘U' : 'Ctrl+U' })}
        variant="text"
        onClick={onClickUnderline}
      />
      <FormattingDropDown disabled={disabled} editor={editor} />
      <IconButton
        ariaLabel={t('link')}
        disabled={disabled}
        isActive={isLink}
        StartIcon={<FontAwesomeIcon icon={faLinkHorizontal} />}
        title={t('link')}
        variant="text"
        onClick={insertLink}
      />
    </div>
  );
};
FontAppearanceFormSection.displayName = 'FontAppearanceFormSection';
